import { useCallback, useRef, useState } from 'react';

import { ApiError } from '../../api/client';
import {
  useReviewAcceptProposal, useReviewEditAcceptProposal, useReviewRejectProposal,
} from '../../hooks/useCrm';
import type { CrmBlockUpdate, CrmRecentUpdates as RecentUpdates } from '../../types';

import { ClampedBlock } from '../shared/ClampedBlock';
import { CrmBlockRuns } from './CrmBlockRuns';
import { blockHasContent, editedBlockValue, groupBlockItems } from './crmBlockGroups';
import styles from './CrmRecentUpdates.module.css';

// Renders the two Recent-Updates blocks natively in the record panel — the deal card's Progress /
// New Info, in-cell. A PENDING block proposal shows its bullets in green with inline Accept / Edit /
// Dismiss (the same in-cell revised framework as scalar fields); accepting routes through the
// lifecycle resolver (settled value + a materialized CRM note). Once accepted, the bullets render plain.
//
// ✎ EDIT (2026-08-24) — drop individual bullets, then accept the rest, via the existing
// `review-edit-accept` door. It closes a real hole rather than adding a convenience: until this, a
// block containing one wrong bullet among several correct ones had no proportionate action. Accept
// published the error firm-wide; Dismiss discarded the correct bullets AND lost them for good (the
// producer's standing set reads PENDING rows only, and the aggregator's event cursor prevents their
// re-emission). The case that found it: a New Info bullet on Marine Equipment Solutions asserting a
// trading update that belonged to Project Pineapple, beside three sound BMO diligence bullets.
// Background + the rest of that programme: docs/plans/strategic-architecture/
// recent-updates-evidence-provenance-and-retraction-spec.md.
//
// E1b-3 S2 (2026-08-22) — the bullets are height-clamped (`ClampedBlock`, 220px, measured), because a
// twenty-bullet Progress run pushed the rest of the panel off screen. ⚠ THIS SURFACE IS NOT BEHIND
// `crm_record_timeline_enabled`: the cluster renders on the live record panel today, so the clamp is
// visible to every user the day it merges — the same "live without a switch" class as the F.5
// evidence-body change, and it must be told to the owner rather than discovered by them.
const BLOCKS: { key: 'progress' | 'new_info'; label: string }[] = [
  { key: 'progress', label: 'Progress' },
  { key: 'new_info', label: 'New Info' },
];

/** E1b-3 D4 — this block's own reading height. Per-surface by design (the timeline row uses 160 and
 * an expanded email body 240): the same run of bullets is read at a different length here, where it
 * is the headline of the record, than it is as one row of a long stream. Owner-adjustable once seen
 * on screen (build plan, open item 3). */
const BLOCK_CLAMP = 220;

interface Props {
  listId: number;
  entryId: number;
  recentUpdates?: RecentUpdates;
  /** Refetch the record after an accept / dismiss (and on a 409 stale). */
  onDone: () => void;
}

// S3 grouped summaries (bounded-emission design §4.3) live in `./crmBlockGroups`, shared with the
// Fields grid's `BlockValue`. They were TWO copies until 2026-08-20, and the copy in the panel never
// grew grouping — so an item with a `group` rendered grouped here and as a flat bullet there, on the
// same record. One implementation is the fix; do not inline a second one.

export function CrmRecentUpdates({ listId, entryId, recentUpdates, onDone }: Props) {
  const accept = useReviewAcceptProposal(listId);
  const editAccept = useReviewEditAcceptProposal(listId);
  const reject = useReviewRejectProposal(listId);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  /** Which block is being edited, and which of its bullets are marked for removal.
   *
   * Held as ONE piece of state keyed by the proposal, not two: an `editing` block plus a free-floating
   * id set can outlive the proposal it was composed against — the operator drops a bullet, a refetch
   * delivers a newer proposal, and the ids now name items that are no longer there. Keying on
   * `proposalId` and clearing on any mismatch makes that unrepresentable. (Same hazard `ClampedBlock`'s
   * key guards for the expand state, one screen down.) */
  const [edit, setEdit] = useState<
    { key: 'progress' | 'new_info'; proposalId: number; dropped: Set<string> } | null>(null);

  /** Takes a THUNK, not a promise. `run(accept.mutateAsync(...))` had already fired the request
   * before the guard could read `inFlight`, so a double-click sent a second POST whose handlers were
   * then dropped — an unhandled rejection and no refetch. The guard has to run before the request
   * exists, which means the caller must not create it. (Same fix as `CrmRecordTimeline.run`, where
   * this was found first.) */
  const run = useCallback((start: () => Promise<unknown>, fallback: string) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setError(null);
    start()
      .then(() => onDone())
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 409) {
          setError('This update changed since you opened it — refreshing to the latest.');
          onDone();
        } else {
          setError(fallback);
        }
      })
      .finally(() => { inFlight.current = false; });
  }, [onDone]);

  if (!recentUpdates) return null;
  const visible = BLOCKS.filter(({ key }) => {
    const b = recentUpdates[key];
    return b && (b.proposal_id != null || blockHasContent(b.resolved));
  });
  if (visible.length === 0) return null;

  return (
    <div className={styles.recentUpdates}>
      <div className={styles.sectionHead}>Recent Updates</div>
      {error && <div className={styles.error} role="alert">{error}</div>}
      {visible.map(({ key, label }) => {
        const b = recentUpdates[key] as CrmBlockUpdate;
        const pending = b.proposal_id != null;
        const groups = groupBlockItems(pending ? b.proposed : b.resolved);
        const pid = b.proposal_id as number;
        // The edit session belongs to ONE block AND one proposal — a newer proposal (or a hop to the
        // other block) leaves it behind rather than re-applying its ids to different content.
        const session = edit != null && edit.key === key && edit.proposalId === pid ? edit : null;
        const editing = session != null;
        const proposed = b.proposed ?? [];
        const kept = session ? editedBlockValue(proposed, session.dropped) : proposed;
        // Nothing dropped ⇒ this is plain Accept, and everything dropped ⇒ this is Dismiss. Both are
        // one click away already, and edit-accept would publish a misleading `edited_accepted` for
        // them, so neither is offered here.
        const canSaveEdit = session != null && kept.length > 0 && kept.length < proposed.length;
        const toggleItem = (itemId: string) => setEdit((prev) => {
          if (prev == null || prev.key !== key || prev.proposalId !== pid) return prev;
          const dropped = new Set(prev.dropped);
          if (!dropped.delete(itemId)) dropped.add(itemId);
          return { ...prev, dropped };
        });
        return (
          <div key={key} className={styles.block}>
            <div className={styles.blockHead}>
              <span className={styles.blockLabel}>{label}</span>
              {pending && editing && (
                <span className={styles.actions}>
                  <button
                    type="button"
                    className={styles.accept}
                    disabled={!canSaveEdit}
                    title={canSaveEdit
                      ? 'Accept the bullets you kept — captures them as a note on the record'
                      : kept.length === 0
                        ? 'Every bullet is removed — use ✗ to dismiss the whole block instead'
                        : 'Remove at least one bullet, or use ✓ Accept to take the block as proposed'}
                    onClick={() => canSaveEdit && run(
                      () => editAccept.mutateAsync({ entryId, proposalId: pid, value: kept })
                        .then((r) => { setEdit(null); return r; }),
                      'Accept failed.')}
                  >
                    ✓ Accept {kept.length} of {proposed.length}
                  </button>
                  <button type="button" title="Discard these changes"
                          onClick={() => setEdit(null)}>Cancel</button>
                </span>
              )}
              {pending && !editing && (
                <span className={styles.actions}>
                  <button
                    type="button"
                    className={styles.accept}
                    title="Accept — captures this as a note on the record"
                    onClick={() => run(() => accept.mutateAsync({ entryId, proposalId: pid }), 'Accept failed.')}
                  >
                    ✓ Accept
                  </button>
                  <button
                    type="button"
                    className={styles.edit}
                    title="Remove individual bullets, then accept the rest"
                    aria-label={`Edit ${label} before accepting`}
                    onClick={() => setEdit({ key, proposalId: pid, dropped: new Set() })}
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    className={styles.dismiss}
                    title="Dismiss this proposed update"
                    onClick={() => run(() => reject.mutateAsync({ entryId, proposalId: pid }), 'Dismiss failed.')}
                  >
                    ✗
                  </button>
                </span>
              )}
            </div>
            {/* E1b-3 D4 — the bullets clamp; ⛔ `blockHead` (the label and, on a pending block, ✓
                Accept, ✎ and ✗) stays OUTSIDE, because a clamp that can hide a decision control is a
                governance bug wearing CSS. The `—` empty state is not clamped: there is nothing to
                collapse and a "Show more" over a dash is a dead control.

                ⛔ AND EDIT MODE IS NOT CLAMPED AT ALL — that same rule, applied one level down. In
                edit mode every bullet carries its own × , so a clamp would hide decision controls
                inside the body rather than beside it: on a long block the operator would compose a
                value out of the bullets they happened to be able to see. Reading is clamped; deciding
                is not.

                ⛔ KEYED BY THE CONTENT'S FULL IDENTITY — the entry AND the proposal — per the
                contract in ClampedBlock's header. `expanded` is instance state and THIS COMPONENT IS
                NOT REMOUNTED when its content changes: the block div's key is the stable 'progress' /
                'new_info', so both a ‹ › hop and an in-place refetch swap the CHILDREN of the same
                instance. `entryId` alone is NOT enough, because the text can change without the
                record changing — the operator expands proposal #77 and dismisses it, the refetch
                delivers #82, and #82's bullets arrive already-expanded: their "I have read this" act
                applied to text they have never seen, on a surface that ships live. `proposal_id`
                (with a `settled` sentinel for the accepted value, which also covers accept →
                settled) closes that. Both swaps are pinned by test. */}
            {groups.length > 0 ? (
              session ? (
                <CrmBlockRuns groups={groups} tone="new" listClassName={styles.bulletsProposed}
                              droppedItemIds={session.dropped} onToggleItem={toggleItem} />
              ) : (
                <ClampedBlock key={`clamp:${entryId}:${key}:${b.proposal_id ?? 'settled'}`}
                              maxHeight={BLOCK_CLAMP} label={label}>
                  <CrmBlockRuns groups={groups} tone={pending ? 'new' : 'plain'}
                                listClassName={pending ? styles.bulletsProposed : styles.bullets} />
                </ClampedBlock>
              )
            ) : (
              <div className={styles.emptyBlock}>—</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default CrmRecentUpdates;
